import { hasValue } from 'Lib/Any';
import { Guid } from 'Lib/Guid';
import { Message } from '../IApiDataModel';

export interface IEquipmentDataModel {
  /** The Serial Number for the equipment item */
  SerialNumber?: string | undefined;
  /** The Make and Model of the equipment item. */
  MakeAndModel?: string | undefined;
  /** The equipment driver */
  Driver?: EquipmentDriverModel | undefined;
  /** Equipment COM port settings */
  PortSettings?: EquipmentPortSettingsModel | undefined;
  /** Equipment item + calibration status */
  Status: EquipmentStatusModel;
  /** Unique identifier for the equipment */
  Id: Guid;
  /** Code of the equipment item */
  Code?: string | undefined;
  /** Validation/warning messages */
  Messages?: Message[] | undefined;
}

/** Represents driver */
export interface EquipmentDriverModel {
  /** QestUUID of Equipment Driver */
  Id: Guid;
  /** Data Model for Equipment Driver */
  Data?: EquipmentDriverDataModel | undefined;
}

/** Equipment Driver Data */
export interface EquipmentDriverDataModel {
  /** The Equipment Driver name */
  Name?: string | undefined;
  /** The Equipment Type Model */
  EquipmentType?: EquipmentTypeModel | undefined;
  /** The Equipment Make */
  EquipmentMake?: string | undefined;
  /** Model No of the Equipment */
  EquipmentModelNo?: string | undefined;
  /** The Year of Manufacture of Equipment. */
  EquipmentYearOfManufacture?: string | undefined;
  /** The Description of Equipment Driver */
  Description?: string | undefined;
  /** The Request of Equipment driver to be sent to the equipment */
  Request?: string | undefined;
  /** The Regular Expression */
  Regex?: string | undefined;
}

/** Represents a type of equipment. */
export interface EquipmentTypeModel {
  /** The unique equipment type id. */
  Id: string;
  /** Equipment name */
  Name: string | undefined;
  /** The type of equipment. */
  EquipmentType: string | undefined;
}

/** Equipment COM port settings */
export interface EquipmentPortSettingsModel {
  /** The Baud Rate in  COM Port Settings Interface */
  BaudRate?: number | undefined;
  /** The Parity in  COM Port Settings Interface */
  Parity?: string | undefined;
  /** The Data Bits in  COM Port Settings Interface */
  DataBits?: number | undefined;
  /** The Stop Bits in  COM Port Settings Interface */
  StopBits?: number | undefined;
  /** The connected COM Port in  COM Port Settings Interface */
  ConnectedComPort?: string | undefined;
  /** is the port setting values valid ? */
  IsValidPortSettings?: boolean | undefined;
}

/** Equipment item + calibration status */
export interface EquipmentStatusModel {
  /** Boolean indicating no calibration tasks were overdue
and at least one calibration was performed before point in time */
  InCalibration: boolean;
  /** Boolean indicating whether any calibration tasks were overdue */
  OutOfCalibration: boolean;
  /** Date that the equipment item was most recently calibrated */
  CalibrationDate?: Date | undefined;
  /** Due date of the calibration */
  CalibrationDueDate?: Date | undefined;
  /** The name of the most recently completed calibration task */
  CalibrationName?: string | undefined;
  /** Details of overdue calibration tasks */
  OverdueCalibrations?: EquipmentCalibrationStatusModel[] | undefined;
}

/** Details of overdue calibration tasks */
export interface EquipmentCalibrationStatusModel {
  /** Unique identifier of the calibration task */
  TaskId: Guid;
  /** The name/description of the calibration task */
  Name?: string | undefined;
  /** The date that the calibration was previously carried out, or
null if the calibration has never been carried out. */
  PreviousCalibrationDate?: Date | undefined;
  /** Date that the calibration was due */
  CalibrationDueDate?: Date | undefined;
  /** The next date that calibration was carried out. Or null if
the calibration wasn't carried out again after the test date. */
  NextCalibrationDate?: Date | undefined;
}

/**
 * Returns true if provided object is recognisable as an equipment data model
 * (by checking exclusive, compulsory properties)
 */
export const isIEquipmentDataModel = (data: any): data is IEquipmentDataModel =>
  hasValue(data?.Status?.InCalibration) && hasValue(data?.Status?.OutOfCalibration);
